import { CandidateStudyProgram } from '~/services/CSPService';

interface Base {
    id: string;
    createdAt: Date;
    updatedAt: Date;
}

/* Candidate */

export interface Candidate extends Base {
    userId: string;

    summary: string | null;

    studyPrograms: CandidateStudyProgram[];
    languages: Language[];
    workExperiences: WorkExperience[];
    memberships: Membership[];
    nationalities: Nationality[];
    skills: Skill[];
    links: Link[];

    gender: Gender;
    pronoun: Pronoun | null;
    ethnicMinority: EthnicMinority | null;
    disability: Disability | null;
    country: string | null;

    profileVisibility: ProfileVisibility | null;
    preferences: CandidatePreferences;

    newsletterConsent: boolean | null;
}

export type WorkPreference =
    | 'internship'
    | 'graduate_program'
    | 'full_time'
    | 'part_time'
    | 'volunteer';

export type WorkLocation = 'onsite' | 'remote' | 'hybrid' | 'remote_hybrid';

export interface CandidatePreferences extends Omit<Base, 'id'> {
    preferredEmployers: string[];
    workPreferences: WorkPreference[];
    eligibleWorkLocation: WorkLocation | null;
    eligibleForRelocation: boolean | null;
    memberOfLGBTQCommunity: boolean | null;

    sectors?: CandidateSector[];
    careerFields?: CandidateCareerField[];
    employers?: Employer[];
}

export interface CandidateSector extends Base {
    candidateId: string;
    sectorId: string;
    sector?: Sector;
}

export interface CandidateCareerField extends Base {
    candidateId: string;
    careerFieldId: string;
    careerField?: CareerField;
}

export interface Language extends Base {
    candidateId: string;

    languageId: string;
    language: string;
    proficiencyId: string;
    proficiency: string;
}

export interface WorkExperience extends Base {
    candidateId: string | null;

    title: string;
    companyTitle: string;
    companyId: number | null;
    location: string | null;
    start: Date;
    end: Date | null;
    description: string | null;
    isCurrentJob: boolean | null;
}

export interface Membership extends Base {
    candidateId: string;

    title: string;
    organizationName: string;

    start: Date;
    end: Date | null;

    description: string | null;
    isActive: boolean | null;
}

export interface Nationality extends Base {
    candidateId: string;
    nationality: string;
}

export interface PredefinedSkill extends Base {
    id: string;
    name: string;
}

export interface Skill extends Base {
    candidateId: string;
    customSkillName: string | null;
    skillId: string | null;
    skill: PredefinedSkill | null;
}

export interface Sector extends Base {
    name: string;
}

export interface CareerField extends Base {
    name: string;
}

export interface Employer extends Base {
    name: string;
    organizationId: number | null;
    isCustom: boolean;
}

export interface Link extends Base {
    candidateId: string;
    name: string;
    value: string;
}

export interface School {
    id: number;
    name: string;
    createdAt: Date | null;
    updatedAt: Date | null;
}

export interface DegreeLevel extends Base {
    name: string;
}
export interface DegreeField extends Base {
    name: string;
}

export type PronounType = 'he/him' | 'she/her' | 'they/them' | 'other' | 'prefer_not_to_say';

export interface Pronoun {
    type: PronounType;
    value: string;
}

// TODO: Fix valid string values for these types.
export type Disability = 'yes' | 'no' | 'prefer_not_to_say';
export type Gender = 'M' | 'F' | 'O' | 'N' | 'B' | 'U';
export type EthnicMinority = 'yes' | 'no' | 'prefer_not_to_say';
export type ProfileVisibility = 'private' | 'public_to_recruiters';

/** Candidate as returned from API */
export interface CandidateResponse {
    id: string;
    user_id: string;

    summary: string | null;
    cv: string | null;
    notification_frequency: string | null;

    languages:
        | {
              id: string;
              candidate_id: string;

              languageId: string;
              language: {
                  id: string;
                  code: string;
                  name: string;
              };
              proficiencyId: string;
              proficiency: {
                  id: string;
                  level: string;
              };

              created_at: string;
              updated_at: string;
          }[]
        | null;
    nationalities: Nationality[] | null;
    skills: Skill[] | null;
    links: Link[] | null;

    studyPrograms: CandidateStudyProgram[];

    workExperiences: WorkExperience[];
    memberships: Membership[];

    github: string | null;
    linkedin: string | null;

    gender: Gender;
    pronoun: Pronoun | null;
    ethnicMinority: string | null;
    disability: string | null;
    country: string | null;

    profileVisibility: ProfileVisibility | null;
    preferences: CandidatePreferences;

    newsletterConsent?: boolean;

    created_at: string;
    updated_at: string;
}

interface DateObj {
    createdAt: string | Date;
    updatedAt: string | Date;
}
const transformDates = <T extends DateObj>(
    obj: T
): Omit<T, 'createdAt' | 'updatedAt'> & {
    createdAt: Date;
    updatedAt: Date;
} => ({
    ...obj,
    createdAt: new Date(obj.createdAt),
    updatedAt: new Date(obj.updatedAt),
});

export const transformCandidate = (res: CandidateResponse): Candidate => ({
    id: res.id,
    userId: res.user_id,

    summary: typeof res.summary === 'undefined' ? null : res.summary,

    createdAt: new Date(res.created_at),
    updatedAt: new Date(res.updated_at),

    gender: res.gender,
    pronoun: res.pronoun,
    ethnicMinority: ['yes', 'no', 'prefer_not_to_say'].includes(res.ethnicMinority || '')
        ? (res.ethnicMinority as EthnicMinority)
        : null,
    disability: ['yes', 'no', 'prefer_not_to_say'].includes(res.disability || '')
        ? (res.disability as Disability)
        : null,
    country: res.country,

    languages: !res.languages
        ? []
        : res.languages.map(lang => ({
              id: lang.id,
              candidateId: lang.candidate_id,

              languageId: lang.languageId,
              language: lang.language.name,
              proficiencyId: lang.proficiencyId,
              proficiency: lang.proficiency.level,

              createdAt: new Date(lang.created_at),
              updatedAt: new Date(lang.updated_at),
          })),
    nationalities: res.nationalities || [],
    skills: (res.skills || []).map(transformDates),
    links: (res.links || []).map(transformDates),

    studyPrograms: (res.studyPrograms || []).map(sp => ({
        ...sp,
        start: new Date(sp.start),
        end: new Date(sp.end),
    })),

    workExperiences: transformWorkExperience(res.workExperiences) || [],
    memberships: transformMembership(res.memberships) || [],
    profileVisibility: res.profileVisibility,
    preferences: (() => {
        const pref = res.preferences || {};
        return {
            createdAt: new Date(pref.createdAt || 0),
            updatedAt: new Date(pref.updatedAt || 0),

            preferredEmployers: pref.preferredEmployers || [],
            workPreferences: pref.workPreferences || [],
            eligibleWorkLocation: pref.eligibleWorkLocation || null,
            eligibleForRelocation: pref.eligibleForRelocation || null,
            memberOfLGBTQCommunity: pref.memberOfLGBTQCommunity || null,

            sectors: pref.sectors || [],
            careerFields: pref.careerFields || [],
            employers: pref.employers || [],
        };
    })(),

    newsletterConsent: res.newsletterConsent !== undefined ? res.newsletterConsent : null,
});

/* WorkAuthorization */

export interface WorkAuthorization {
    id: string;
    candidateId: string;
    code: string;
    implicit: boolean;
    type: number;
}

export interface WorkAuthorizationResponse {
    id: string;
    candidate_id: string;
    code: string;
    implicit: boolean;
    type: number;
}

export const transformWorkAuthorization = (res: WorkAuthorizationResponse): WorkAuthorization => ({
    id: res.id,
    candidateId: res.candidate_id,
    code: res.code,
    implicit: res.implicit,
    type: res.type,
});

export const transformWorkExperience = (res: WorkExperience[]): WorkExperience[] => {
    if (!res) return [];

    return res.map(work => ({
        ...work,
        start: new Date(new Date(work.start).toISOString().slice(0, 7) + '-01'),
        end: work.end ? new Date(new Date(work.end).toISOString().slice(0, 7) + '-01') : null,
    }));
};
/* Skill */

export interface PredefinedSkillResponse {
    results: PredefinedSkill[];
}

export const transformPredefinedSkills = (res: PredefinedSkillResponse): PredefinedSkill[] =>
    res.results.map(skill => ({
        id: skill.id,
        name: skill.name,
        createdAt: new Date(skill.createdAt),
        updatedAt: new Date(skill.updatedAt),
    }));

export const transformMembership = (res: Membership[]): Membership[] => {
    if (!res) return [];

    return res.map(membership => ({
        ...membership,
        start: new Date(new Date(membership.start).toISOString().slice(0, 7) + '-01'),
        end: membership.end
            ? new Date(new Date(membership.end).toISOString().slice(0, 7) + '-01')
            : null,
    }));
};
